import { useState, useRef } from "react"
import { FormProvider, useForm } from "react-hook-form"
import ReCAPTCHA from "react-google-recaptcha"
import isEmail from "validator/lib/isEmail"
import isMobilePhone from "validator/lib/isMobilePhone"
import getInputValidationClasses from "@/lib/helpers/getInputValidationClasses"
import { input } from "@/common/form/input.module.css"
import InputGroup from "@/common/form/inputGroup"
import Label from "@/common/form/label"
import { ErrorMessage } from "../errorMessage"
import useCheckoutContext, {
  useUserDetails,
  usePaymentAmount,
  useMembershipAmount,
  useToken,
  useActiveStep
} from "../store"
import CompanyDetails from "./companyDetails"
import EditUserDetails from "./editUserDetails"
import PersonalDetails from "./personalDetails"
import PinInputField from "./pinInputField"
import SubmitStepButton from "./submitStepButton"
import { addOrderLog, requestOrderIntent } from "@/lib/api/checkoutForm"
import { cleanEmail } from "@/lib/helpers/email"
import { Terms } from "@/components/checkout-form/payment/payment"
import Personnummer from "personnummer"

function UserDetails({
  defaultValues,
  step: { isCompany },
  step,
  orderSessionId,
  formSettings
}) {
  const { setNextActiveStep } = useCheckoutContext()
  const { userDetails, setUserDetails } = useUserDetails()
  const [editingUserDetails, setEditingUserDetails] = useState(false)
  const [isStepValidOverride, setIsStepValidOverride] = useState(false)
  const { paymentAmount, setPaymentAmount } = usePaymentAmount()
  const { setMembershipAmount } = useMembershipAmount()
  const { checkYouth } = useActiveStep()

  const { token } = useToken()

  const recaptchaRef = useRef()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

  if (defaultValues) {
    userDetails.pin = "" || userDetails?.pin || ""
    userDetails.email = userDetails?.email || defaultValues.email || ""
    userDetails.phone = userDetails?.phone || defaultValues.phone || ""
  }

  const methods = useForm({
    defaultValues: {
      pin: userDetails?.pin || "",
      email: userDetails?.email || "",
      phone: userDetails?.phone || ""
    },
    mode: "onTouched",
    shouldUnregister: true
  })

  const { register, handleSubmit, control, formState, watch } = methods

  const pin = watch("pin")

  const onSubmit = async (values) => {
    const captcha = await recaptchaRef.current.executeAsync()

    if (formSettings?.membershipAmounts?.member !== undefined) {
      const hasTypeStep = formSettings?.steps.find(
        (step) => step.name === "membershipType"
      )
      let newMembershipAmount = false

      if (!token) {
        newMembershipAmount = formSettings.membershipAmounts.family_member
      }

      if (
        // This only triggers when making sure age is under 26
        checkYouth ||
        // This handles annualMembership changing membership to youthMembership
        (hasTypeStep && Personnummer.parse(values.pin).getAge() < 26)
      ) {
        newMembershipAmount = formSettings.membershipAmounts.youth_member
      }

      if (newMembershipAmount) {
        setMembershipAmount(newMembershipAmount)
      }

      setPaymentAmount(paymentAmount.amountValue)

      if (formSettings.type === "annualMembership") {
        newMembershipAmount =
          checkYouth && Personnummer.parse(values.pin).getAge() < 26
            ? formSettings.membershipAmounts.youth_member
            : formSettings.membershipAmounts.family_member

        setMembershipAmount(newMembershipAmount)
        setPaymentAmount(newMembershipAmount)
      }
    }

    // Track step
    addOrderLog(orderSessionId, "Fyllt i personuppgifter", values)

    if (formSettings?.intentTag) {
      requestOrderIntent({
        ...values,
        pin: Personnummer.parse(values.pin).format(true),
        captcha,
        intent: true,
        formId: formSettings.id,
        path: window.location.pathname
      })
    }

    setUserDetails(values)
    setNextActiveStep()
  }

  if (isCompany) {
    return <CompanyDetails />
  }

  return (
    <FormProvider
      {...methods}
      setEditingUserDetails={setEditingUserDetails}
      setIsStepValidOverride={setIsStepValidOverride}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ReCAPTCHA
          className="hidden"
          ref={recaptchaRef}
          size="invisible"
          sitekey={siteKey}
        />
        <div className="flex flex-wrap -mx-3">
          <PinInputField
            orderSessionId={orderSessionId}
            tooltip={step.helpTexts.pin}
          />
          {editingUserDetails ? <EditUserDetails /> : <PersonalDetails />}
          <div className="w-full px-3 mb-3 sm:w-1/2">
            <InputGroup>
              <Label name="email" tooltip={step.helpTexts.email}>
                E-postadress
              </Label>
              <input
                autoComplete="email"
                className={[
                  input,
                  getInputValidationClasses("email", control)
                ].join(" ")}
                id="email"
                {...register("email", {
                  onBlur: (e) => {
                    methods.setValue("email", cleanEmail(e.target.value))
                  },
                  required: true,
                  validate: (value) =>
                    isEmail(value) || "Ange en giltig e-postadress"
                })}
              />
              <ErrorMessage name="email" />
            </InputGroup>
          </div>
          <div className="w-full px-3 mb-3 sm:w-1/2">
            <InputGroup>
              <Label name="phone" tooltip={step.helpTexts.phone}>
                Mobilnummer
              </Label>
              <input
                id="phone"
                inputMode="tel"
                autoComplete="phone mobile mobilenumber"
                className={[
                  input,
                  getInputValidationClasses("phone", control)
                ].join(" ")}
                {...register("phone", {
                  required: true,
                  validate: (value) =>
                    !value ||
                    isMobilePhone(value, "sv-SE") ||
                    "Ange ett giltigt mobiltelefonnummer."
                })}
                placeholder="Endast siffror, ex. 0700000000"
              />
              <ErrorMessage name="phone" />
            </InputGroup>
          </div>
          {step.isValidText !== "" &&
            formState.isValid &&
            Personnummer.valid(pin) &&
            Personnummer.parse(pin).getAge() > 25 && (
              <div className="px-4 py-4 mx-3 mb-3 rounded-sm bg-summer-green-50">
                {step.isValidText}
              </div>
            )}
          <div className="mx-3 mb-6 text-sm rounded-sm">
            <Terms />
          </div>
        </div>
        <SubmitStepButton
          overrideDisabled={!isStepValidOverride}
          loading={formState.isSubmitting}
        />
      </form>
    </FormProvider>
  )
}

export default UserDetails
